import HttpService from '@/common/services/HttpService'
import { getTeamCasesMetadata } from '@/common/services/TeamsService'
import { getCustomerCasesMetadata } from '@/common/services/CustomerService'
import { getCompanyCasesMetadata } from '@/common/services/CompanyService'
import {
  CASE_COMPANY_CARDS,
  CASE_CUSTOMER_CARDS,
  CASE_DETAILS_CARDS,
  type CaseCardsCodes,
  type CaseDetailsDTOV1,
  type Case,
  type CaseActionTypeDTOV1,
  type CaseDTOV3,
  type CaseActionParamsDTOV1,
  type CaseFieldConfig,
  type CasesMetadataDTOV1,
  type CasesMetadataIdentifier,
  CaseMetadataError,
  type WorklogDTO,
} from '@/common/types/case'
import type { CaseScriptDTOV1 } from '@/common/types/survey'
import { EntityFieldCodeEnum } from '@/common/types/fields'
import { EntityEnum } from '@/common/types/general'
import { OperationEnum } from '@/search/types/search'

export const getCase = async (caseId: number, { includes, expands }: CaseFieldConfig = {}): Promise<CaseDTOV3> => {
  const params = {
    include: includes?.size ? [...includes].join() : undefined,
    expand: expands?.size ? [...expands].join() : undefined,
  }
  return HttpService.getData<CaseDTOV3>(`/api/v3/cases/${caseId}`, {
    params,
  })
}

export const getCaseWorklog = async (caseId: number) => {
  return HttpService.post<WorklogDTO>(`/api/v2/cases/${caseId}/worklogs`)
}

export const getCaseSkillExternalUrl = async (caseId: number): Promise<string> => {
  return HttpService.getData<string>(`/api/v3/cases/${caseId}/skill-external-url`)
}

export const getCaseClipboard = async (caseId: number): Promise<string> => {
  return HttpService.getData<string>(`/api/v1/cases/${caseId}/clipboard`)
}

export const getCaseScripts = async (caseId: number): Promise<CaseScriptDTOV1[]> => {
  return HttpService.getData<CaseScriptDTOV1[]>(`/api/v1/cases/${caseId}/scripts`)
}

export const getBulkCases = async (ids?: number[], { includes, expands }: CaseFieldConfig = {}): Promise<CaseDetailsDTOV1[]> => {
  const params = {
    include: includes?.size ? [...includes].join() : undefined,
    expand: expands?.size ? [...expands].join() : undefined,
  }
  return HttpService.postData<CaseDetailsDTOV1[]>(`/api/v1/bulk-cases/fetch`, { ids }, { params })
}

export const getCaseActionTypes = (params: CaseActionParamsDTOV1) => {
  return HttpService.getData<CaseActionTypeDTOV1[]>('/api/v1/cases/action-types', {
    params,
  })
}

export const getChildrenCasesMetadata = (caseId: number) => {
  return HttpService.getData<CasesMetadataDTOV1>(`/api/v1/cases/${caseId}/children-metadata`)
}

export const getCaseListingMetadata = (cardCode: CaseCardsCodes, identifier: CasesMetadataIdentifier) => {
  switch (cardCode) {
    case CASE_DETAILS_CARDS.CHILD_CASES:
      if (typeof identifier !== 'number') throw new CaseMetadataError(CASE_DETAILS_CARDS.CHILD_CASES)
      return getChildrenCasesMetadata(identifier)
    case CASE_DETAILS_CARDS.TEAM_CASES:
      if (typeof identifier !== 'string') throw new CaseMetadataError(CASE_DETAILS_CARDS.TEAM_CASES)
      return getTeamCasesMetadata(identifier)
    case CASE_CUSTOMER_CARDS.CUSTOMER_CASES:
      if (typeof identifier !== 'number') throw new CaseMetadataError(CASE_CUSTOMER_CARDS.CUSTOMER_CASES)
      return getCustomerCasesMetadata(identifier)
    case CASE_COMPANY_CARDS.COMPANY_CASES:
      if (typeof identifier !== 'number') throw new CaseMetadataError(CASE_COMPANY_CARDS.COMPANY_CASES)
      return getCompanyCasesMetadata(identifier)
    default:
      throw new CaseMetadataError(cardCode)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isCase(entity: any): entity is Case {
  return (entity as Case).currentSkillCode !== undefined
}

export const cancelTicketCreation = () => {
  return HttpService.get(`ticket/create/cancel`)
}

export const updateCaseCustomer = (caseId: number, customerId: number) => {
  return HttpService.patchData(`/api/v3/cases/${caseId}/customer`, {
    customerId,
  })
}

export const updateCaseCompany = (caseId: number, companyIds: number[]) => {
  return HttpService.patchData(`/api/v3/cases/${caseId}/company`, {
    companyIds,
  })
}

export const getCaseCompanies = async (caseId: number): Promise<number[]> => {
  return HttpService.postData<number[]>('/search/api/v1/specs', {
    entity: EntityEnum.COMPANY,
    max: 100,
    fields: [
      {
        code: EntityFieldCodeEnum.TICKET_ID,
        op: OperationEnum.eq,
        values: [caseId.toString()],
      },
    ],
    criteria: [],
    sort: [],
  })
}
